<template>
  <div class="editorial-f-wrap">

    <header class="ed-header">
      <div class="left-section">
        <button type="button" class="hamburger-menu" aria-label="Toggle menu">☰</button>
        <nav class="lang-switcher" aria-label="Language">
          <button type="button" class="lang-btn active" aria-pressed="true">EN</button>
          <span class="lang-sep" aria-hidden="true">|</span>
          <button type="button" class="lang-btn" aria-pressed="false">DE</button>
          <span class="lang-sep" aria-hidden="true">|</span>
          <button type="button" class="lang-btn" aria-pressed="false">FR</button>
          <span class="lang-sep" aria-hidden="true">|</span>
          <button type="button" class="lang-btn" aria-pressed="false">IT</button>
        </nav>
      </div>
      <div class="brand">
        <span class="brand-title">Swiss Eating Quiz</span>
        <span class="brand-version">Beta · 03.05.2026</span>
      </div>
    </header>

    <div class="screen-wrap">
      <main class="screen" role="main" aria-labelledby="section-heading">

        <div class="checkmark-wrap">
          <div class="checkmark-circle">
            <svg class="checkmark-svg" viewBox="0 0 28 28" aria-hidden="true">
              <polyline points="7,15 12,20 21,9"/>
            </svg>
          </div>
          <div class="checkmark-rule"></div>
        </div>

        <p class="eyebrow">Abschnitt abgeschlossen</p>

        <h1 id="section-heading" class="title">Schön <em>gemacht</em></h1>

        <p class="subtitle">
          Du hast alle Fragen zum Thema {{ completedSection }} beantwortet.
          Auf zum nächsten Kapitel.
        </p>

        <div
          class="progress-section"
          role="progressbar"
          :aria-valuenow="doneCount"
          aria-valuemin="0"
          :aria-valuemax="sections.length"
        >
          <p class="progress-label">Fortschritt</p>
          <div class="progress-row">
            <div
              v-for="s in sections"
              :key="s.title"
              class="progress-segment"
              :class="s.status"
              :title="s.title"
            ></div>
          </div>
          <p class="progress-text">
            <strong>{{ doneWritten }} von {{ totalWritten }}</strong> Abschnitten geschafft
          </p>
        </div>

        <div class="fact-card">
          <p class="fact-label">Wusstest du?</p>
          <p class="fact-text">
            Eine Portion Frucht entspricht etwa <strong>120 g</strong>. Das ist
            eine mittelgrosse Frucht, eine Handvoll Beeren oder ein Becher
            geschnittene Früchte.
          </p>
        </div>

        <div class="up-next">
          <div class="up-next-meta">
            <p class="up-next-label">Als Nächstes</p>
            <p class="up-next-section">{{ upNext.section }}</p>
          </div>
          <span class="up-next-count">{{ upNext.questions }} Fragen</span>
        </div>

        <div class="footer">
          <button type="button" class="continue-button" @click="goNext">
            Weiter →
          </button>
        </div>

      </main>
    </div>
  </div>
</template>

<script>
const WRITTEN_NUMBERS = ['Null', 'Eins', 'Zwei', 'Drei', 'Vier', 'Fünf', 'Sechs', 'Sieben', 'Acht', 'Neun', 'Zehn'];

function written(n) {
  return WRITTEN_NUMBERS[n] || String(n);
}

export default {
  name: 'EditorialF',
  data() {
    return {
      completedSection: 'Früchte',
      sections: [
        { title: 'Gemüse', status: 'done' },
        { title: 'Früchte', status: 'done' },
        { title: 'Tierische Proteine', status: 'current' },
        { title: 'Pflanzliche Proteine', status: '' },
        { title: 'Getreide', status: '' },
        { title: 'Milchprodukte', status: '' },
        { title: 'Getränke', status: '' },
      ],
      upNext: { section: 'Tierische Proteine', questions: 8 },
    };
  },
  computed: {
    doneCount() {
      return this.sections.filter(s => s.status === 'done').length;
    },
    doneWritten() {
      return written(this.doneCount);
    },
    totalWritten() {
      return written(this.sections.length).toLowerCase();
    },
  },
  methods: {
    goNext() {
      this.$router.push('/postsurvey/email');
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,300;9..144,400;9..144,500;9..144,600&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@400;500&display=swap');

.editorial-f-wrap {
  --swiss-red: #DA291C;
  --swiss-red-deep: #B01E13;
  --ink: #1A1A1A;
  --ink-soft: #2D2D2D;
  --ink-mute: #6B6B6B;
  --ink-faint: #9C9C9C;
  --paper: #FAF7F2;
  --paper-warm: #F2EDE4;
  --paper-card: #FFFFFF;
  --rule-soft: #E5E0D7;
  --good: #2D8659;
  --good-soft: rgba(45, 134, 89, 0.12);
  --font-display: "Fraunces", Georgia, serif;
  --font-body: "Inter", -apple-system, BlinkMacSystemFont, sans-serif;
  --font-mono: "JetBrains Mono", "SF Mono", Consolas, monospace;

  font-family: var(--font-body);
  background: var(--paper);
  color: var(--ink);
  line-height: 1.55;
  font-size: 16px;
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;
  min-height: 100%;
  box-sizing: border-box;
}

.editorial-f-wrap *,
.editorial-f-wrap *::before,
.editorial-f-wrap *::after {
  box-sizing: border-box;
}

.ed-header {
  border-bottom: 1px solid var(--ink);
  background: var(--paper);
  padding: 12px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.left-section {
  display: flex;
  align-items: center;
  gap: 16px;
}

.hamburger-menu {
  background: transparent;
  border: 0.5px solid var(--rule-soft);
  color: var(--ink);
  font-size: 18px;
  width: 32px;
  height: 32px;
  border-radius: 4px;
  cursor: pointer;
}

.lang-switcher {
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: var(--font-mono);
  font-size: 11px;
  letter-spacing: 0.04em;
}

.lang-btn {
  background: transparent;
  border: none;
  color: var(--ink-faint);
  font-family: inherit;
  font-size: inherit;
  cursor: pointer;
  padding: 2px 4px;
}

.lang-btn.active {
  color: var(--swiss-red);
  font-weight: 600;
}

.lang-sep { color: var(--ink-faint); }

.brand {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  line-height: 1.2;
}

.brand-title {
  font-family: var(--font-display);
  font-size: 16px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: var(--ink);
}

.brand-version {
  font-family: var(--font-mono);
  font-size: 10px;
  color: var(--ink-mute);
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.screen-wrap {
  display: flex;
  justify-content: center;
  padding: 40px 20px;
}

.screen {
  width: 100%;
  max-width: 520px;
  display: flex;
  flex-direction: column;
}

.checkmark-wrap {
  margin-bottom: 32px;
  display: flex;
  align-items: center;
  gap: 16px;
}

.checkmark-circle {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: transparent;
  border: 1px solid var(--swiss-red);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  animation: pop 0.5s cubic-bezier(0.18, 0.89, 0.32, 1.28) both;
  position: relative;
}

.checkmark-circle::before {
  content: "";
  position: absolute;
  inset: -4px;
  border-radius: 50%;
  border: 1px solid var(--swiss-red);
  opacity: 0;
  animation: ripple 1.4s ease-out 0.3s infinite;
}

.checkmark-svg {
  width: 26px;
  height: 26px;
  stroke: var(--swiss-red);
  stroke-width: 2.5;
  stroke-linecap: round;
  stroke-linejoin: round;
  fill: none;
  stroke-dasharray: 50;
  stroke-dashoffset: 50;
  animation: draw 0.4s ease-out 0.3s forwards;
}

.checkmark-rule {
  flex: 1;
  height: 1px;
  background: var(--swiss-red);
  animation: rule-grow 0.5s ease-out 0.4s both;
  transform-origin: left;
}

@keyframes pop {
  0% { transform: scale(0); opacity: 0; }
  100% { transform: scale(1); opacity: 1; }
}

@keyframes ripple {
  0% { transform: scale(1); opacity: 0.5; }
  100% { transform: scale(1.4); opacity: 0; }
}

@keyframes draw {
  to { stroke-dashoffset: 0; }
}

@keyframes rule-grow {
  0% { transform: scaleX(0); }
  100% { transform: scaleX(1); }
}

.eyebrow {
  font-family: var(--font-mono);
  font-size: 11px;
  color: var(--swiss-red);
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin-bottom: 16px;
  animation: fade-up 0.5s ease-out 0.5s both;
}

.title {
  font-family: var(--font-display);
  font-size: clamp(56px, 11vw, 84px);
  font-weight: 400;
  line-height: 0.95;
  letter-spacing: -0.03em;
  margin: 0 0 24px;
  color: var(--ink);
  font-variation-settings: "opsz" 144;
  animation: fade-up 0.5s ease-out 0.6s both;
}

.title em {
  font-style: italic;
  font-weight: 300;
  color: var(--swiss-red);
}

.subtitle {
  font-family: var(--font-display);
  font-size: 19px;
  font-weight: 400;
  line-height: 1.5;
  color: var(--ink-soft);
  margin: 0 0 40px;
  max-width: 440px;
  font-variation-settings: "opsz" 24;
  animation: fade-up 0.5s ease-out 0.7s both;
}

@keyframes fade-up {
  0% { opacity: 0; transform: translateY(12px); }
  100% { opacity: 1; transform: translateY(0); }
}

.progress-section {
  border-top: 1px solid var(--rule-soft);
  border-bottom: 1px solid var(--rule-soft);
  padding: 24px 0;
  margin-bottom: 32px;
  animation: fade-up 0.5s ease-out 0.8s both;
}

.progress-label {
  font-family: var(--font-mono);
  font-size: 11px;
  color: var(--ink-mute);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin: 0 0 14px;
}

.progress-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
}

.progress-segment {
  height: 4px;
  flex: 1;
  background: var(--paper-warm);
  border: 0.5px solid var(--rule-soft);
  transition: all 0.3s;
  position: relative;
}

.progress-segment.done {
  background: var(--swiss-red);
  border-color: var(--swiss-red);
}

.progress-segment.current {
  background: var(--paper-warm);
  border-color: var(--swiss-red);
}

.progress-segment.current::after {
  content: "";
  position: absolute;
  inset: -1px;
  border: 1px solid var(--swiss-red);
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}

.progress-text {
  font-family: var(--font-display);
  font-size: 16px;
  color: var(--ink);
  font-style: italic;
  margin: 0;
}

.progress-text strong {
  font-style: normal;
  font-weight: 500;
  color: var(--swiss-red);
}

.fact-card {
  background: var(--paper-warm);
  border-left: 2px solid var(--swiss-red);
  padding: 24px 28px;
  margin-bottom: 32px;
  animation: fade-up 0.5s ease-out 0.95s both;
}

.fact-label {
  font-family: var(--font-mono);
  font-size: 10px;
  color: var(--swiss-red);
  letter-spacing: 0.1em;
  text-transform: uppercase;
  font-weight: 600;
  margin: 0 0 10px;
}

.fact-text {
  font-family: var(--font-display);
  font-size: 19px;
  line-height: 1.45;
  color: var(--ink);
  font-style: italic;
  font-weight: 400;
  font-variation-settings: "opsz" 24;
  margin: 0;
}

.fact-text strong {
  font-style: normal;
  font-weight: 500;
  color: var(--swiss-red);
}

.up-next {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 0;
  border-top: 0.5px solid var(--rule-soft);
  border-bottom: 0.5px solid var(--rule-soft);
  margin-bottom: 32px;
  animation: fade-up 0.5s ease-out 1.05s both;
}

.up-next-meta {
  flex: 1;
}

.up-next-label {
  font-family: var(--font-mono);
  font-size: 10px;
  color: var(--ink-mute);
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin: 0 0 4px;
}

.up-next-section {
  font-family: var(--font-display);
  font-size: 28px;
  font-weight: 500;
  line-height: 1.1;
  color: var(--ink);
  letter-spacing: -0.01em;
  font-variation-settings: "opsz" 144;
  margin: 0;
}

.up-next-count {
  font-family: var(--font-mono);
  font-size: 11px;
  color: var(--ink-mute);
  letter-spacing: 0.06em;
  text-transform: uppercase;
  flex-shrink: 0;
}

.footer {
  margin-top: auto;
  animation: fade-up 0.5s ease-out 1.15s both;
}

.continue-button {
  width: 100%;
  padding: 18px;
  background: var(--ink);
  color: var(--paper);
  border: 1px solid var(--ink);
  border-radius: 0;
  font-size: 13px;
  font-family: var(--font-mono);
  font-weight: 500;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}

.continue-button:hover {
  background: var(--swiss-red);
  border-color: var(--swiss-red);
}
</style>
